import * as globals from '@wandb/common/css/globals.styles';
import React, {FC, memo} from 'react';
import styled from 'styled-components';

import {useBetaFeatureNightModeEnabled} from '../../util/useBetaFeature';
import {LocalSearchResultType, NightAwareProps} from '.';
import {MOBILE_BREAKPOINT_PX} from './utils';

export type ResultFilterMode = `all` | LocalSearchResultType;

type TabOption = {
  label: string;
  value: ResultFilterMode;
};

const tabOptions: TabOption[] = [
  {label: `All results`, value: `all`},
  {label: `Projects`, value: `project`},
  {label: `Reports`, value: `report`},
  {label: `Teams`, value: `team`},
];

type FilterTabsProps = {
  filterMode: ResultFilterMode;
  disabledModes: Set<ResultFilterMode>;
  onSelect: (m: ResultFilterMode) => void;
};

const FilterTabsComp: FC<FilterTabsProps> = ({
  filterMode,
  disabledModes,
  onSelect,
}) => {
  const isNightMode = useBetaFeatureNightModeEnabled();

  return (
    <TabsContainer>
      {tabOptions.map(({label, value}, i) => {
        const disabled = disabledModes.has(value);
        return (
          <Tab
            key={i}
            isNightMode={isNightMode}
            active={value === filterMode}
            disabled={disabled}
            onClick={() => {
              if (!disabled) {
                onSelect(value);
              }
            }}>
            {label}
          </Tab>
        );
      })}
    </TabsContainer>
  );
};

export const FilterTabs = memo(FilterTabsComp);

const TabsContainer = styled.div`
  display: flex;
  margin: 24px 0 0;
  border-bottom: 1px solid ${globals.GRAY_350};

  @media (max-width: ${MOBILE_BREAKPOINT_PX}px) {
    display: none;
  }
`;

type TabProps = {
  active: boolean;
  disabled: boolean;
} & NightAwareProps;

const Tab = styled.div.attrs({
  className: `night-aware`,
})<TabProps>`
  position: relative;
  top: 1px;
  font-weight: 600;
  padding-bottom: 12px;
  ${p => !p.disabled && `cursor: pointer;`}
  &:not(:first-child) {
    margin-left: 36px;
  }

  color: ${p => (!p.isNightMode ? globals.GRAY_860 : globals.GRAY_50)};
  ${p =>
    p.active
      ? `
        border-bottom: 2px solid ${globals.TEAL};
      `
      : p.disabled
      ? `
        color: ${!p.isNightMode ? globals.GRAY_400 : globals.GRAY_700};
      `
      : `&:not(:hover) {
        color: ${!p.isNightMode ? globals.GRAY_500 : globals.GRAY_600};
      }`}
`;
